import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { PaidModuleSchema } from './ops.js';
import { IsoDateSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * ADM-07 — SUBSCRIPTION & BILLING, the CUSTOMER'S SIDE.
 *
 * "This screen lets you see what you pay PalAccount, what you are using, and
 * switch plan. You use it when you are running out of seats, or when the
 * finance office asks for last month's receipt."
 *
 * ── WHAT IS BUILT, AND WHY IT IS READ-ONLY ─────────────────────────────────
 *
 * The first half of that sentence is built. The second is not, and its absence
 * is a RULE rather than a gap.
 *
 * Owner ruling R21 fixes exactly two ways a company gets access: an online
 * card payment, or an operator grant. There is no third. The operator console
 * already owns granting, extending and revoking (PART 22 §22.5), and the
 * online route is closed until DECISION.md §8.7 says who the payment provider
 * is — `PAYMENT_PROVIDER` defaults to `none` and `none` throws, deliberately,
 * because a silently-mocked payment provider produces "a customer who believes
 * they have paid, a subscription granted for money that never moved, and a
 * reconciliation nobody can do afterwards".
 *
 * So a **Switch plan** button here would be a third route to access that R21
 * does not have, and one the customer controls. There is no such route in this
 * contract and there must not be until there is a provider behind it. THE
 * DATABASE AGREES AND IS THE REAL GUARANTEE: `app_user` — the credential the
 * tenant product connects as — holds SELECT and nothing else on
 * `tenant_subscriptions`, so a company cannot extend its own subscription
 * whatever its application code does. Every write goes through `ops_user`,
 * from a caller the customer does not control (a webhook the provider signs,
 * or an operator).
 *
 * ── WHAT THE SPEC ASKS FOR THAT IS DEFERRED, EACH WITH ITS REASON ──────────
 *
 *   §4's THREE PLAN CARDS and their prices ($49/$149/$399) — there is no price
 *     catalogue. 17.5.2: `tenants.plan` is "free text; there is no payment
 *     provider behind it". Drawing three prices would be inventing a
 *     commercial commitment, and BR-ADM-07-03 ("switching plan must not
 *     rewrite past invoices") shows the spec already knows what a wrong number
 *     on this screen costs. This is also where F-68 (plan + add-on prices)
 *     and F-73 (monthly total = plan + Σ enabled add-ons) are DELIBERATELY
 *     not built: both are arithmetic over a price catalogue that does not
 *     exist yet, F-68's own caution records that the three sources disagree
 *     about seats (EC-15-15), and F-73's defect (current total shown against
 *     past invoices, EC-15-20) is exactly what building it without a stored
 *     history would reproduce. Owner decision + payment provider first.
 *
 *   The SEAT-CAP and PRICE disagreements (EC-15-15's 10/50/unlimited vs
 *     5/25/200 vs 10/50/250, EC-15-22's $29/$99/custom vs $49/$149/$399) —
 *     both are owner decisions on numbers this build deliberately does not
 *     draw; the only seat truth today is `tenants.seat_limit`, set by the
 *     operator per tenant. EC-15-16 (a money setting must name its
 *     currency, and say whether it converts) is the same class of owner
 *     decision — its proposed answer (base currency, converting at the
 *     document's rate) is recorded and not yet ruled.
 *
 *   §4's BILLING HISTORY (`PAY-YYYY-MM`, four statuses) — there is no billing
 *     document anywhere in the platform: no table, no numbering, no payment
 *     record. §7 says so itself — "Only Paid exists in the product", and
 *     EC-15-19 records that the other three "are needed and missing". The
 *     history arrives with the provider that generates it.
 *
 *   §4's MANAGE PAYMENT METHOD — the same. It is a provider's hosted page.
 *
 *   §5's COMPANIES METER — a tenant IS a company in this build. Owner ruling
 *     R19 replaced ADM-09 with the operator console, and a customer cannot
 *     create a second company from inside the product: PART 22 §22.5 A is the
 *     only door, and an operator walks through it. A meter reading `1 / 1`
 *     that nothing could ever move would be a decoration.
 *
 *   §5's MODULE ADD-ON SWITCHES — §5 calls them "a reflection rather than a
 *     control" in the same breath as calling them switches, and the reflection
 *     half is what is true here: `tenant_entitlements` is written by the
 *     console (BR-OPS-02-04) and read by `EntitlementsGuard` on every request.
 *     A customer-side toggle would be the customer selling themselves a
 *     module, which is the same R21 violation as switching plan.
 *
 * WHAT IS LEFT IS WORTH HAVING ON ITS OWN: a company can finally see what it
 * has, when it runs out, which modules it bought, and — the number that
 * actually bites — how many of its seats are gone. BR-ADM-05-12 is enforced,
 * so that last figure is real rather than decorative.
 */
// ── The meters (§5 field 4, F-81) ───────────────────────────────────────────
/**
 * BR-ADM-07-06 — "A meter at or above nine-tenths must be red, and at or above
 * three-quarters amber."
 *
 * Computed on the SERVER and sent as a word, not left to the client to derive
 * from a ratio. Two reasons: the thresholds are a business rule with an ID, so
 * they belong where rules live; and a meter with no limit has no band at all,
 * which a client dividing by `null` would have to special-case anyway.
 */
export const METER_BANDS = ['normal', 'warning', 'full'];
export const MeterBandSchema = z.enum(METER_BANDS);
/**
 * F-72 — the usage meter: `fill = used ÷ limit`, red at 90%, amber at 75%,
 * brand colour below. A limitless meter is a figure, not a bar.
 */
export function meterBand(used, limit) {
    if (limit === null || limit <= 0)
        return 'normal';
    const ratio = used / limit;
    if (ratio >= 0.9)
        return 'full';
    if (ratio >= 0.75)
        return 'warning';
    return 'normal';
}
export const UsageMeterSchema = z
    .object({
    /** `seats` · `documents` · `storage`. The client draws each one's label. */
    id: z.enum(['seats', 'documents', 'storage']),
    used: z.number().int().min(0),
    /**
     * NULL means "this plan publishes no allowance for this", and the client
     * draws a figure rather than a bar. Only `seats` has a real limit today
     * (`tenants.seat_limit`, which BR-ADM-05-12 enforces on every invitation);
     * the other two are counted honestly and capped by nothing, because there
     * is no price catalogue that says what they should be capped at.
     *
     * A ZERO WOULD BE A LIE — it would draw a full red bar on a company that
     * has bought nothing wrong. Null is the honest shape.
     */
    limit: z.number().int().min(0).nullable(),
    band: MeterBandSchema,
})
    .strict();
export const SubscriptionViewSchema = z
    .object({
    /**
     * R21's three states, exactly as the sign-in gate and the console see them
     * — one definition of "current", re-evaluated on every read rather than
     * stamped by a job (`subscriptions.reader.ts` argues that at length).
     */
    state: z.enum(['current', 'expired', 'none']),
    /** 17.5.2 — free text. Shown as given; never matched against a price list. */
    plan: z.string().nullable(),
    periodStart: IsoDateSchema.nullable(),
    /** §5 field 3's "Renews" — the day access runs out if nothing is done. */
    periodEnd: IsoDateSchema.nullable(),
    /**
     * HOW the company got its access (R21's two routes plus the migration's
     * backfill). Shown, because a customer whose subscription came from a
     * phone call needs to know that renewing means another phone call — and
     * because it is the honest answer to "why is there no card on file?".
     */
    grantedVia: z.enum(['online_payment', 'operator_grant', 'backfill']).nullable(),
    /**
     * §5 field 5/6 — the add-ons, "a reflection rather than a control". Both
     * halves of the gate are reported: `sold` is what the period covers,
     * `enabled` is the console's per-module switch, and `EntitlementsGuard`
     * requires both. They are separate fields because they can genuinely
     * disagree — a module sold and then switched off is a support action, not
     * a data error, and a screen that merged them could not explain it.
     */
    modules: z
        .object({
        module: PaidModuleSchema,
        sold: z.boolean(),
        enabled: z.boolean(),
    })
        .strict()
        .array(),
    meters: UsageMeterSchema.array(),
})
    .strict();
export const SUBSCRIPTION_VIEW_MESSAGES = {
    /** ERR-ADM-07-05 — an admin who reaches the route. BR-PERM-69's sentence. */
    ownerOnly: 'Only the account owner can see billing.',
    /**
     * §12's required sentence, verbatim: "the connection to the accounts is a
     * sentence a build must add to the screen". It is here rather than in the
     * dictionary alone because it is a SPEC STRING — the screen must not
     * paraphrase it into "see Purchases for your bills".
     */
    billsLiveUnderPurchases: 'Your PalAccount invoices appear under Purchases as bills from PalAccount.',
};
export const subscriptionContract = c.router({
    /**
     * ONE ROUTE, and it is a GET. Everything ADM-07 could write is either an
     * operator action (PART 22 §22.5) or a payment provider's (§8.7) — see the
     * file header. A POST here would be R21's third route to access.
     */
    view: {
        method: 'GET',
        path: '/api/subscription',
        responses: { 200: SubscriptionViewSchema, 401: MessageSchema, 403: MessageSchema },
        summary: 'ADM-07: the company’s own subscription — plan, period, modules and usage. Read-only (R21). The Owner alone (BR-PERM-69, BR-ADM-07-09).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=subscription.js.map